<template>
  <fieldset :id="id" class="add-stop-edit">
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :input-id="`${id}-location`"
      :label="locationInputLabel"
      :errors="v$.location.$errors"
    >
      <LocationQuickSearch
        :id="inputId"
        v-bind="ariaProps"
        v-model="internalLocation"
        :class="inputClasses"
        :append-to="appendTo"
        :invalid="invalid"
        :autofocus="autofocus"
        :placeholder="locationInputLabel"
        @clear="onClear"
        @keypress.enter="onEnterInDropdownInput"
      />
    </InputWrapper>

    <InputWrapper
      v-if="stopSelector === 'intermediate' && !stopType"
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :input-id="`${id}-stop-type`"
      :label="stopTypeInputLabel"
      :errors="v$.stopType.$errors"
    >
      <RygenDropdown
        :id="inputId"
        v-model="internalStopType"
        v-bind="ariaProps"
        :append-to="appendTo"
        :class="inputClasses"
        :invalid="invalid"
        :options="stopTypeOptions"
        option-label="label"
        option-value="value"
        :placeholder="stopTypeInputLabel"
      />
    </InputWrapper>

    <div
      v-if="requiresConfirmation"
      class="flex flex-gap-1 justify-content-end border-top"
    >
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        mode="secondary"
        type="submit"
        :label="t('common.done')"
        @click.prevent="onConfirm"
      />
    </div>
  </fieldset>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useValidations } from '@rygen/rygen-ui/composables';
import { Location, Optional } from '@rygen/rygen-ui/model';

import { EmbeddableLocation, ShipmentStopType } from '@/model';
import {
  AddStopPayload,
  SHIPMENT_STOP_TYPES_BY_STOP_SELECTOR,
  StopSelector,
  TRANSLATIONS_BY_STOP_SELECTOR,
} from '@/model/routeBuilder';

import LocationQuickSearch from '@/components/location/LocationQuickSearch.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  location: {
    type: Object as PropType<Location>,
    default: () => undefined,
  },
  stopType: {
    type: String as PropType<ShipmentStopType>,
    default: () => undefined,
  },
  stopSelector: {
    type: String as PropType<StopSelector>,
    default: () => 'intermediate',
  },
  locationLabel: {
    type: Function as PropType<(defaultLabel: string) => string>,
    default: () => undefined,
  },
  stopTypeLabel: {
    type: Function as PropType<(defaultLabel: string) => string>,
    default: () => undefined,
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  /**
   * Determines if update events are only emitted on user confirmation
   *
   * When this prop is true, we render confirmation buttons and short-circuit
   * two-way binding. Update events will **not** be emitted on changes.
   * Instead a consumer will need to listen for the `confirm` event.
   */
  requiresConfirmation: {
    type: Boolean,
    default: () => false,
  },
  autofocus: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  location,
  stopType,
  stopSelector,
  locationLabel,
  stopTypeLabel,
  appendTo,
  requiresConfirmation,
  autofocus,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'confirm', payload: AddStopPayload): void;
  (event: 'update:location', newVal: Location): void;
  (event: 'update:stop-type', newVal: ShipmentStopType): void;
}>();

const { t } = useI18n();

const internalLocation: Ref<Optional<Location>> = ref(
  location?.value ? new Location(location.value) : undefined,
);
const internalStopType = ref(stopType?.value);

const defaultStopTypeLabel = computed(() => {
  if (stopSelector.value === 'intermediate' && stopType?.value) {
    return stopType.value === ShipmentStopType.PICKUP
      ? t('shipments.pickups')
      : t('shipments.dropoffs');
  } else {
    return TRANSLATIONS_BY_STOP_SELECTOR[stopSelector.value];
  }
});

const locationInputLabel = computed(() => {
  const base = t('common.newParam', { param: defaultStopTypeLabel.value });
  return locationLabel?.value?.(base) ?? base;
});

const stopTypeInputLabel = computed(() => {
  const base = t('common.paramTypes', { param: t('shipments.stops') });
  return stopTypeLabel?.value?.(base) ?? base;
});

const internalValue = computed(() => ({
  location: internalLocation.value
    ? EmbeddableLocation.fromLocation(internalLocation.value)
    : undefined,
  stopType: internalStopType.value,
}));

const rules = computed(() => ({
  location: {
    required: required(locationInputLabel.value),
  },
  stopType: {
    required: required(stopTypeInputLabel.value),
  },
}));
const { v$, validate } = useValidations(internalValue, rules);

const stopTypeOptions = computed(() => [
  {
    label: t('enumMap.ShipmentStopType.PICKUP'),
    value: ShipmentStopType.PICKUP,
  },
  {
    label: t('enumMap.ShipmentStopType.DROP_OFF'),
    value: ShipmentStopType.DROP_OFF,
  },
]);

const onEnterInDropdownInput = (event: KeyboardEvent) => {
  // Stop the keyboard event from bubbling up to the popup card so that the
  // popup isn't closed.
  event.stopPropagation();
};

const onClear = () => {
  internalLocation.value = undefined;
  v$.value.location.$reset();
};

const onConfirm = async () => {
  if (!(await validate())) {
    return;
  }

  // validation ensures these are defined
  emit('confirm', {
    location: internalValue.value.location!,
    stopType: internalValue.value.stopType!,
    stopSelector: stopSelector.value,
  });
};

const onCancel = () => {
  emit('cancel');
};

watch(
  stopSelector,
  (newVal) => {
    if (!internalStopType.value) {
      internalStopType.value = SHIPMENT_STOP_TYPES_BY_STOP_SELECTOR[newVal];
    }
  },
  { immediate: true },
);

watch(
  () => location?.value,
  (newVal) => {
    internalLocation.value = newVal ? new Location(newVal) : undefined;
  },
  { deep: true, immediate: true },
);

watch(
  () => internalLocation.value?.id,
  (newVal) => {
    if (
      newVal &&
      !requiresConfirmation.value &&
      newVal !== location?.value?.id
    ) {
      // The condition above guarantees `internalLocation` is defined
      emit('update:location', internalLocation.value!);
    }
  },
);

watch(
  () => stopType?.value,
  (newVal) => {
    internalStopType.value =
      newVal ?? SHIPMENT_STOP_TYPES_BY_STOP_SELECTOR[stopSelector.value];
  },
  { deep: true, immediate: true },
);

watch(internalStopType, (newVal) => {
  if (newVal && !requiresConfirmation.value && !isEqual(newVal, stopType)) {
    emit('update:stop-type', newVal);
  }
});
</script>

<style>
.add-stop-edit {
  display: flex;
  flex-direction: column;
  gap: var(--gap-1);
  margin: 0;
  padding: 0;
  border: none;
}
</style>
